// src/utils/migrations/auditOperationalAccessProfiles.js

import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
} from "firebase/firestore";

import { auth, db } from "../../firebase";

import {
  PLATFORM_ROLES,
  getRolePermissions,
  normalizeId,
  normalizePlatformRole,
} from "../../security/permissions";

/*
 * =========================================================
 * SHIFTBRIDGE OPERATIONAL ACCESS AUDIT
 * =========================================================
 *
 * READ ONLY.
 *
 * This file DOES NOT:
 * - create accessProfiles
 * - update users
 * - update facilityProfiles
 * - update roster mirrors
 * - change permissions
 *
 * It compares:
 *
 * users/{uid}
 *      ↓
 * accessProfiles/{uid}
 *      ↓
 * facilityProfiles/{facilityId}
 *      ↓
 * planners/{uid} or workers/{uid}, where applicable
 */

const OPERATIONAL_ROLES = new Set([
  PLATFORM_ROLES.FACILITY_PLANNER,
  PLATFORM_ROLES.FACILITY_CLIENT,
  PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  PLATFORM_ROLES.WORKER,
]);

/* =========================================================
   NORMALIZATION
   ========================================================= */

function uniqueIds(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map(normalizeId)
        .filter(Boolean)
        .filter((value) => value !== "all")
    )
  ).sort();
}

function sameIdLists(a = [], b = []) {
  const left = uniqueIds(a);
  const right = uniqueIds(b);

  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (value, index) =>
      value === right[index]
  );
}

function getCanonicalFacilityIds(data = {}) {
  return uniqueIds([
    data?.primaryFacilityId,
    data?.facilityId,

    ...(Array.isArray(data?.facilityIds)
      ? data.facilityIds
      : []),

    ...(Array.isArray(
      data?.allowedFacilityIds
    )
      ? data.allowedFacilityIds
      : []),
  ]);
}

function getLegacyFacilityIds(data = {}) {
  return uniqueIds([
    data?.assignedFacilityId,

    ...(Array.isArray(
      data?.assignedFacilityIds
    )
      ? data.assignedFacilityIds
      : []),
  ]);
}

function getPrimaryFacilityId(data = {}) {
  const value =
    normalizeId(
      data?.primaryFacilityId ||
        data?.facilityId ||
        ""
    );

  if (!value || value === "all") {
    return "";
  }

  return value;
}

function getNormalizedRole(data = {}) {
  return normalizePlatformRole(
    data?.platformRole ||
      data?.role ||
      ""
  );
}

function getCredentialsShape(user = {}) {
  if (Array.isArray(user?.credentials)) {
    return "array";
  }

  if (
    user?.credentials &&
    typeof user.credentials === "object"
  ) {
    return "legacy_map";
  }

  if (user?.credentials == null) {
    return "missing";
  }

  return typeof user.credentials;
}

function getWorkerOperationalState(user = {}) {
  return String(
    user?.workerOperationalState || ""
  )
    .trim()
    .toUpperCase();
}

/* =========================================================
   WORKER READINESS
   ========================================================= */

function isWorkerOperationallyReady(
  user = {}
) {
  const status =
    normalizeId(user?.status);

  const onboardingStatus =
    normalizeId(
      user?.onboardingStatus
    );

  const complianceStatus =
    normalizeId(
      user?.complianceStatus
    );

  const workerStatus =
    normalizeId(
      user?.workerStatus
    );

  const operationalState =
    getWorkerOperationalState(user);

  return (
    status === "active" &&
    (
      onboardingStatus === "approved" ||
      user?.onboardingCompleted === true
    ) &&
    (
      complianceStatus === "approved" ||
      complianceStatus === "ready"
    ) &&
    (
      workerStatus === "ready" ||
      operationalState === "READY"
    )
  );
}

/* =========================================================
   PERMISSION AUDIT
   ========================================================= */

function permissionAudit(
  role,
  actualPermissions
) {
  const expected =
    getRolePermissions(role) || {};

  const actual =
    actualPermissions &&
    typeof actualPermissions === "object" &&
    !Array.isArray(actualPermissions)
      ? actualPermissions
      : {};

  const expectedKeys =
    Object.keys(expected).sort();

  const actualKeys =
    Object.keys(actual).sort();

  const missingKeys =
    expectedKeys.filter(
      (key) =>
        !Object.prototype.hasOwnProperty.call(
          actual,
          key
        )
    );

  const extraKeys =
    actualKeys.filter(
      (key) =>
        !Object.prototype.hasOwnProperty.call(
          expected,
          key
        )
    );

  const wrongValues =
    expectedKeys
      .filter(
        (key) =>
          Object.prototype.hasOwnProperty.call(
            actual,
            key
          ) &&
          actual[key] !== expected[key]
      )
      .map((key) => ({
        key,
        expected: expected[key],
        actual: actual[key],
      }));

  return {
    ok:
      missingKeys.length === 0 &&
      extraKeys.length === 0 &&
      wrongValues.length === 0,

    missingKeys,
    extraKeys,
    wrongValues,
  };
}

/* =========================================================
   PRIMARY STATUS
   ========================================================= */

function choosePrimaryStatus(issues = []) {
  const priority = [
    "MISSING_ACCESS_PROFILE",
    "MISSING_CANONICAL_TENANT_ID",
    "MISSING_CANONICAL_FACILITY_SCOPE",
    "DUPLICATE_EMAIL_IDENTITY",
    "ROLE_MISMATCH",
    "TENANT_MISMATCH",
    "FACILITY_SCOPE_MISMATCH",
    "MISSING_FACILITY_PROFILE",
    "FACILITY_TENANT_MISMATCH",
    "PERMISSION_MISMATCH",
    "ACCESS_PROFILE_SCHEMA_MISMATCH",
    "MISSING_ROSTER_MEMBERSHIP",
    "NOT_OPERATIONALLY_READY",
  ];

  for (const key of priority) {
    if (issues.includes(key)) {
      return key;
    }
  }

  return "CANONICAL";
}

/* =========================================================
   ROSTER INDEX
   ========================================================= */

async function loadRosterIndex(
  facilityIds
) {
  const plannerIndex = new Map();
  const workerIndex = new Map();
  const readErrors = [];

  for (const facilityId of facilityIds) {
    for (const collectionName of [
      "planners",
      "workers",
    ]) {
      try {
        const snap =
          await getDocs(
            collection(
              db,
              "facilityProfiles",
              facilityId,
              collectionName
            )
          );

        const target =
          collectionName === "planners"
            ? plannerIndex
            : workerIndex;

        snap.docs.forEach((item) => {
          target.set(
            `${facilityId}:${item.id}`,
            {
              id: item.id,
              facilityId,
              ...item.data(),
            }
          );
        });
      } catch (error) {
        readErrors.push({
          facilityId,
          collectionName,
          code: error?.code || "",
          message:
            error?.message ||
            String(error),
        });
      }
    }
  }

  return {
    plannerIndex,
    workerIndex,
    readErrors,
  };
}

/* =========================================================
   MAIN AUDIT
   ========================================================= */

export default async function
auditOperationalAccessProfiles({ tenantId: requestedTenantId } = {}) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Audit requires an authenticated administrator.");
  const [callerSnap, platformSnap] = await Promise.all([
    getDoc(doc(db, "users", uid)), getDoc(doc(db, "platformAdmins", uid)),
  ]);
  if (auth.currentUser?.uid !== uid) throw new Error("Audit authentication changed.");
  const caller = callerSnap.exists() ? callerSnap.data() : {};
  const platform = platformSnap.exists() ? platformSnap.data() : {};
  const trusted = platform.active === true && platform.disabled !== true && platform.role === "platform_admin";
  const tenantId = requestedTenantId === undefined ? caller.tenantId : requestedTenantId;
  const primaryRole = caller.platformRole || caller.role;
  const consistentRoles = !caller.platformRole || !caller.role || caller.platformRole === caller.role;
  const validAdmin = caller.active === true && caller.disabled !== true && caller.archived !== true &&
    caller.status === "active" && consistentRoles && ["owner_admin", "admin", "operations_admin"].includes(primaryRole);
  if (typeof tenantId !== "string" || tenantId === "all" || !/^[a-z0-9][a-z0-9_-]*$/.test(tenantId) ||
      (!trusted && (!validAdmin || caller.tenantId !== tenantId))) {
    throw new Error("Audit stopped: valid tenant administrative authority is required.");
  }
  const scopeLimitation = "Tenant-scoped audit only: this cannot prove platform-wide integrity or discover every record with a missing or incorrectly assigned tenant ID.";
  console.warn(scopeLimitation);
  console.log(
    "SHIFTBRIDGE OPERATIONAL ACCESS AUDIT — START"
  );

  /*
   * READ ONLY.
   */
  const [
    usersSnap,
    accessSnap,
    facilitiesSnap,
  ] = await Promise.all([
    getDocs(
      query(collection(db, "users"), where("tenantId", "==", tenantId))
    ),

    getDocs(
      query(collection(db, "accessProfiles"), where("tenantId", "==", tenantId))
    ),

    getDocs(
      query(collection(db, "facilityProfiles"), where("tenantId", "==", tenantId))
    ),
  ]);

  /* =======================================================
     ACCESS PROFILE INDEX
     ======================================================= */

  const accessByUid = new Map();

  accessSnap.docs.forEach((item) => {
    accessByUid.set(
      item.id,
      {
        id: item.id,
        ...item.data(),
      }
    );
  });

  /* =======================================================
     FACILITY INDEX
     ======================================================= */

  const facilitiesById = new Map();

  facilitiesSnap.docs.forEach((item) => {
    const data =
      item.data() || {};

    const facilityId = item.id;

    if (!facilityId) {
      return;
    }

    facilitiesById.set(
      facilityId,
      {
        id: item.id,
        facilityId,
        ...data,
      }
    );
  });

  const facilityIds =
    Array.from(
      facilitiesById.keys()
    );

  /* =======================================================
     ROSTER INDEX
     ======================================================= */

  const {
    plannerIndex,
    workerIndex,
    readErrors: rosterReadErrors,
  } = await loadRosterIndex(
    facilityIds
  );

  /* =======================================================
     DUPLICATE IDENTITY INDEX
     ======================================================= */

  const emailToUids = new Map();

  usersSnap.docs.forEach((userDoc) => {
    const user =
      userDoc.data() || {};

    const uid =
      String(
        user?.uid ||
          userDoc.id ||
          ""
      ).trim();

    const email =
      String(
        user?.email || ""
      )
        .trim()
        .toLowerCase();

    if (!email) {
      return;
    }

    const existing =
      emailToUids.get(email) || [];

    emailToUids.set(
      email,
      Array.from(
        new Set([
          ...existing,
          uid,
        ])
      )
    );
  });

  /* =======================================================
     OPERATIONAL ACCOUNT AUDIT
     ======================================================= */

  const rows = [];

  for (const userDoc of usersSnap.docs) {
    const user =
      userDoc.data() || {};

    /*
     * These values MUST be inside the main loop.
     */
    const credentialsShape =
      getCredentialsShape(user);

    const workerOperationalState =
      getWorkerOperationalState(
        user
      );

    const uid =
      String(
        user?.uid ||
          userDoc.id ||
          ""
      ).trim();

    const email =
      String(
        user?.email || ""
      )
        .trim()
        .toLowerCase();

    const role =
      getNormalizedRole(user);

    if (
      !OPERATIONAL_ROLES.has(role)
    ) {
      continue;
    }

    const issues = [];

    /* =====================================================
       DUPLICATE IDENTITY
       ===================================================== */

    const duplicateIdentityUids =
      email
        ? emailToUids.get(email) || []
        : [];

    const hasDuplicateEmailIdentity =
      duplicateIdentityUids.length > 1;

    if (hasDuplicateEmailIdentity) {
      issues.push(
        "DUPLICATE_EMAIL_IDENTITY"
      );
    }

    /* =====================================================
       USER CANONICAL SCOPE
       ===================================================== */

    const tenantId =
      normalizeId(
        user?.tenantId
      );

    const legacyTenantId =
      normalizeId(
        user?.tenantKey
      );

    const userFacilityIds =
      getCanonicalFacilityIds(user);

    const legacyFacilityIds =
      getLegacyFacilityIds(user);

    const primaryFacilityId =
      getPrimaryFacilityId(user) ||
      userFacilityIds[0] ||
      "";

    const access =
      accessByUid.get(uid) || null;

    /* =====================================================
       WORKER LIFECYCLE
       ===================================================== */

    const workerOperationallyReady =
      role === PLATFORM_ROLES.WORKER
        ? isWorkerOperationallyReady(
            user
          )
        : false;

    if (
      role ===
        PLATFORM_ROLES.WORKER &&
      !workerOperationallyReady
    ) {
      issues.push(
        "NOT_OPERATIONALLY_READY"
      );
    }

    /* =====================================================
       TENANT / FACILITY SOURCE DATA
       ===================================================== */

    if (
      !tenantId ||
      tenantId === "all"
    ) {
      issues.push(
        "MISSING_CANONICAL_TENANT_ID"
      );
    }

    if (
      userFacilityIds.length === 0
    ) {
      issues.push(
        "MISSING_CANONICAL_FACILITY_SCOPE"
      );
    }

    /* =====================================================
       FACILITY PROFILE INTEGRITY
       ===================================================== */

    for (
      const facilityId of userFacilityIds
    ) {
      const facility =
        facilitiesById.get(
          facilityId
        );

      if (!facility) {
        issues.push(
          "MISSING_FACILITY_PROFILE"
        );

        continue;
      }

      const facilityTenantId =
        normalizeId(
          facility?.tenantId
        );

      if (
        tenantId &&
        tenantId !== "all" &&
        facilityTenantId &&
        tenantId !==
          facilityTenantId
      ) {
        issues.push(
          "FACILITY_TENANT_MISMATCH"
        );
      }
    }

    /* =====================================================
       ACCESS PROFILE
       ===================================================== */

    let accessRole = "";
    let accessTenantId = "";
    let accessFacilityIds = [];
    let accessPrimaryFacilityId = "";
    let permissionCheck = null;

    if (!access) {
      /*
       * Non-ready workers should not yet
       * have operational authority.
       */
      if (
        role !==
          PLATFORM_ROLES.WORKER ||
        workerOperationallyReady
      ) {
        issues.push(
          "MISSING_ACCESS_PROFILE"
        );
      }
    } else {
      accessRole =
        getNormalizedRole(access);

      accessTenantId =
        normalizeId(
          access?.tenantId
        );

      accessFacilityIds =
        getCanonicalFacilityIds(
          access
        );

      accessPrimaryFacilityId =
        getPrimaryFacilityId(
          access
        ) ||
        accessFacilityIds[0] ||
        "";

      /* ---------------------------------------------------
         ROLE
         --------------------------------------------------- */

      if (
        accessRole !== role
      ) {
        issues.push(
          "ROLE_MISMATCH"
        );
      }

      /* ---------------------------------------------------
         TENANT
         --------------------------------------------------- */

      if (
        tenantId &&
        tenantId !== "all" &&
        accessTenantId !== tenantId
      ) {
        issues.push(
          "TENANT_MISMATCH"
        );
      }

      /* ---------------------------------------------------
         FACILITY SCOPE
         --------------------------------------------------- */

      if (
        !sameIdLists(
          userFacilityIds,
          accessFacilityIds
        ) ||
        (
          primaryFacilityId &&
          accessPrimaryFacilityId !==
            primaryFacilityId
        )
      ) {
        issues.push(
          "FACILITY_SCOPE_MISMATCH"
        );
      }

      /* ---------------------------------------------------
         CANONICAL ACCESS PROFILE SCHEMA
         --------------------------------------------------- */

      const accessRoleField =
        normalizePlatformRole(
          access?.role || ""
        );

      const accessPlatformRoleField =
        normalizePlatformRole(
          access?.platformRole || ""
        );

      const accessSchemaIsCanonical =
        access?.uid === uid &&
        normalizeId(
          access?.email
        ) === email &&
        accessRoleField === role &&
        accessPlatformRoleField ===
          role &&
        access?.active === true &&
        access?.disabled === false &&
        normalizeId(
          access?.status
        ) === "active" &&
        Number(
          access?.scopeVersion
        ) === 1 &&
        Number(
          access?.permissionVersion
        ) === 1;

      if (!accessSchemaIsCanonical) {
        issues.push(
          "ACCESS_PROFILE_SCHEMA_MISMATCH"
        );
      }

      /* ---------------------------------------------------
         PERMISSIONS
         --------------------------------------------------- */

      permissionCheck =
        permissionAudit(
          role,
          access?.permissions
        );

      if (!permissionCheck.ok) {
        issues.push(
          "PERMISSION_MISMATCH"
        );
      }
    }

    /* =====================================================
       LEGACY ROSTER MIRROR
       ===================================================== */

    const missingRosterFacilityIds =
      [];

    if (
      role ===
      PLATFORM_ROLES.FACILITY_PLANNER
    ) {
      userFacilityIds.forEach(
        (facilityId) => {
          if (
            !plannerIndex.has(
              `${facilityId}:${uid}`
            )
          ) {
            missingRosterFacilityIds.push(
              facilityId
            );
          }
        }
      );
    }

    if (
      role ===
        PLATFORM_ROLES.WORKER &&
      workerOperationallyReady
    ) {
      userFacilityIds.forEach(
        (facilityId) => {
          if (
            !workerIndex.has(
              `${facilityId}:${uid}`
            )
          ) {
            missingRosterFacilityIds.push(
              facilityId
            );
          }
        }
      );
    }

    if (
      missingRosterFacilityIds.length >
      0
    ) {
      issues.push(
        "MISSING_ROSTER_MEMBERSHIP"
      );
    }

    const uniqueIssues =
      Array.from(
        new Set(issues)
      );

    /* =====================================================
       REPAIR DISPOSITION
       ===================================================== */

    let repairDisposition =
      "SAFE_AUTO_REPAIR";

    const repairReasons = [];

    /*
     * Worker lifecycle takes precedence.
     * We never create operational access for
     * a worker who is not operationally ready.
     */
    if (
      role ===
        PLATFORM_ROLES.WORKER &&
      !workerOperationallyReady
    ) {
      repairDisposition =
        "NOT_OPERATIONALLY_READY";

      repairReasons.push(
        "Worker lifecycle is not yet operationally ready."
      );
    }

    /*
     * Legacy credentials map must be cleaned
     * before worker roster regeneration.
     */
    else if (
      role ===
        PLATFORM_ROLES.WORKER &&
      credentialsShape ===
        "legacy_map"
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "Worker still has legacy credentials map; clean credentials before rebuilding roster membership."
      );
    }

    /*
     * Identity collision requires manual review.
     */
    else if (
      hasDuplicateEmailIdentity
    ) {
      repairDisposition =
        "MANUAL_IDENTITY_REVIEW";

      repairReasons.push(
        `Email is shared by ${duplicateIdentityUids.length} user identities.`
      );
    }

    /*
     * Tenant cannot be invented.
     */
    else if (
      !tenantId ||
      tenantId === "all"
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "Canonical users/{uid}.tenantId is missing or invalid."
      );
    }

    /*
     * Facility scope cannot be invented.
     */
    else if (
      userFacilityIds.length === 0
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "Canonical facility scope is missing from users/{uid}."
      );
    }

    /*
     * Facility must exist.
     */
    else if (
      uniqueIssues.includes(
        "MISSING_FACILITY_PROFILE"
      )
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "One or more referenced facility profiles do not exist."
      );
    }

    /*
     * User tenant and facility tenant must agree.
     */
    else if (
      uniqueIssues.includes(
        "FACILITY_TENANT_MISMATCH"
      )
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "User tenant and facility tenant do not agree."
      );
    }

    /*
     * Role disagreement is identity/security sensitive.
     */
    else if (
      uniqueIssues.includes(
        "ROLE_MISMATCH"
      )
    ) {
      repairDisposition =
        "MANUAL_IDENTITY_REVIEW";

      repairReasons.push(
        "users/{uid} role and accessProfiles/{uid} role disagree."
      );
    }

    /*
     * Existing access tenant disagreement must not
     * be silently overwritten.
     */
    else if (
      uniqueIssues.includes(
        "TENANT_MISMATCH"
      )
    ) {
      repairDisposition =
        "BLOCKED_SOURCE_DATA";

      repairReasons.push(
        "users/{uid} tenant and access profile tenant disagree."
      );
    }

    /*
     * Remaining defects are derived operational
     * authorization defects and may be rebuilt from
     * canonical user + facility source data.
     */
    else {
      if (
        uniqueIssues.includes(
          "MISSING_ACCESS_PROFILE"
        )
      ) {
        repairReasons.push(
          "Create missing canonical access profile."
        );
      }

      if (
        uniqueIssues.includes(
          "PERMISSION_MISMATCH"
        )
      ) {
        repairReasons.push(
          "Replace legacy permission map with canonical role permissions."
        );
      }

      if (
        uniqueIssues.includes(
          "ACCESS_PROFILE_SCHEMA_MISMATCH"
        )
      ) {
        repairReasons.push(
          "Canonicalize access profile schema/version fields."
        );
      }

      if (
        uniqueIssues.includes(
          "FACILITY_SCOPE_MISMATCH"
        )
      ) {
        repairReasons.push(
          "Rebuild access-profile facility scope from canonical users/{uid} scope."
        );
      }

      if (
        uniqueIssues.includes(
          "MISSING_ROSTER_MEMBERSHIP"
        )
      ) {
        repairReasons.push(
          "Rebuild required facility roster membership."
        );
      }

      if (
        uniqueIssues.length === 0
      ) {
        repairDisposition =
          "CANONICAL";

        repairReasons.push(
          "No repair required."
        );
      }
    }

    /* =====================================================
       REPORT ROW
       ===================================================== */

    const status =
      choosePrimaryStatus(
        uniqueIssues
      );

    rows.push({
      status,

      repairDisposition,
      repairReasons,

      issues:
        uniqueIssues,

      duplicateEmailIdentity:
        hasDuplicateEmailIdentity,

      duplicateIdentityUids,

      uid,
      email,
      role,

      tenantId,
      legacyTenantId,

      primaryFacilityId,
      userFacilityIds,
      legacyFacilityIds,

      hasAccessProfile:
        Boolean(access),

      accessRole,
      accessTenantId,

      accessPrimaryFacilityId,
      accessFacilityIds,

      accessActive:
        access?.active,

      accessDisabled:
        access?.disabled,

      accessStatus:
        normalizeId(
          access?.status
        ),

      scopeVersion:
        access?.scopeVersion,

      permissionVersion:
        access?.permissionVersion,

      accessSource:
        access?.source || "",

      permissionCheck,

      missingRosterFacilityIds,

      credentialsShape,
      workerOperationalState,

      userStatus:
        normalizeId(
          user?.status
        ),

      workerStatus:
        normalizeId(
          user?.workerStatus
        ),

      onboardingStatus:
        normalizeId(
          user?.onboardingStatus
        ),

      complianceStatus:
        normalizeId(
          user?.complianceStatus
        ),
    });
  }

  /* =======================================================
     SORT REPORT
     ======================================================= */

  rows.sort((a, b) => {
    const dispositionCompare =
      String(
        a.repairDisposition
      ).localeCompare(
        String(
          b.repairDisposition
        )
      );

    if (
      dispositionCompare !== 0
    ) {
      return dispositionCompare;
    }

    const statusCompare =
      String(
        a.status
      ).localeCompare(
        String(
          b.status
        )
      );

    if (
      statusCompare !== 0
    ) {
      return statusCompare;
    }

    return String(
      a.email || a.uid
    ).localeCompare(
      String(
        b.email || b.uid
      )
    );
  });

  /* =======================================================
     SUMMARY
     ======================================================= */

  const summary =
    rows.reduce(
      (acc, row) => {
        acc.total += 1;

        /*
         * Status totals.
         */
        acc.byStatus[row.status] =
          (
            acc.byStatus[
              row.status
            ] || 0
          ) + 1;

        /*
         * Repair disposition totals.
         */
        acc.byDisposition[
          row.repairDisposition
        ] =
          (
            acc.byDisposition[
              row.repairDisposition
            ] || 0
          ) + 1;

        /*
         * Canonical vs attention.
         */
        if (
          row.repairDisposition ===
          "CANONICAL"
        ) {
          acc.canonical += 1;
        } else {
          acc.needsAttention += 1;
        }

        return acc;
      },
      {
        total: 0,
        canonical: 0,
        needsAttention: 0,
        byStatus: {},
        byDisposition: {},
      }
    );

  console.log(
    "SHIFTBRIDGE OPERATIONAL ACCESS AUDIT — SUMMARY",
    summary
  );

  console.table(
    rows.map((row) => ({
      disposition:
        row.repairDisposition,

      status:
        row.status,

      email:
        row.email,

      role:
        row.role,

      tenantId:
        row.tenantId,

      facilityId:
        row.primaryFacilityId,

      credentials:
        row.credentialsShape,

      access:
        row.hasAccessProfile
          ? "YES"
          : "NO",

      issues:
        row.issues.join(", "),
    }))
  );

  /* =======================================================
     ROSTER READ WARNINGS
     ======================================================= */

  if (
    rosterReadErrors.length > 0
  ) {
    console.warn(
      "SHIFTBRIDGE OPERATIONAL ACCESS AUDIT — ROSTER READ ERRORS",
      rosterReadErrors
    );
  }

  /* =======================================================
     FINAL REPORT
     ======================================================= */

  const report = {
    tenantId,
    scope: "tenant",
    scopeLimitation,
    generatedAt:
      new Date().toISOString(),

    summary,
    rows,
    rosterReadErrors,
  };

  console.log(
    "SHIFTBRIDGE OPERATIONAL ACCESS AUDIT — COMPLETE",
    report
  );

  return report;
}